/**
 * StaffNoteChangeCard - Card for staff note entity changes.
 *
 * Displays note content, agent tags, and metadata.
 */

import { StickyNote } from 'lucide-react';

import { UserLink } from '@/components/entity-links/entities';
import { Badge } from '@/components/ui/catalyst/badge';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';

import { useChangedFields } from '../utils/useChangedFields';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import { useEntityChangeActions } from './EntityChangeActionsContext';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';

const KEY_FIELDS: FieldConfig[] = [
  { key: 'created_at', label: 'Created', isDate: true },
  { key: 'modified_at', label: 'Modified', isDate: true },
];


interface StaffNoteChangeCardProps {
  entity: EntityChangeSchema;
}

export const StaffNoteChangeCard: React.FC<StaffNoteChangeCardProps> = ({
  entity,
}) => {
  const changes = useChangedFields(
    entity.change_type,
    entity.changed_fields ?? [],
  );
  const actions = useEntityChangeActions();
  const effectiveStatus =
    actions?.statusOverrides[entity.id as string] ?? entity.status;

  const snapshot =
    entity.change_type === 'deleted' ? entity.old_snapshot : entity.new_snapshot;
  const data = (snapshot?.data ?? {}) as Record<string, unknown>;
  const content = (data.content as string) || '';
  const agentTags = (data.agent_tags as string[]) ?? [];
  const contentSnippet =
    content.length > 80 ? `${content.slice(0, 80)}…` : content;

  const showContentInBody =
    entity.change_type === 'created' || entity.change_type === 'deleted';

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={contentSnippet || 'Staff Note'}
      icon={StickyNote}
      iconBgClassName="bg-amber-50"
      iconTextClassName="text-amber-600"
      entityStatus={effectiveStatus}
      onStatusChange={
        actions
          ? (newStatus) => actions.onStatusChange(entity.id as string, newStatus)
          : undefined
      }
      badges={agentTags.map((tag) => (
        <Badge key={tag} color="zinc" className="text-xs">
          {tag}
        </Badge>
      ))}
      changeCount={changes.totalChanges}
      hasExpandedContent
    >
      {/* Content */}
      {(showContentInBody || changes.isFieldChanged('content')) && (
        changes.isFieldChanged('content') ? (
          <ChangedFieldHighlight
            label="Content"
            fieldKey="content"
            changes={changes}
          >
            <p className="text-sm text-gray-900 whitespace-pre-wrap">
              {content}
            </p>
          </ChangedFieldHighlight>
        ) : (
          <div className="px-1 py-0.5">
            <span className="text-xs text-gray-400">Content:</span>
            <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">
              {content}
            </p>
          </div>
        )
      )}

      {/* Agent tags (if changed) */}
      {changes.isFieldChanged('agent_tags') && agentTags.length > 0 && (
        <ChangedFieldHighlight
          label="Agent Tags"
          fieldKey="agent_tags"
          changes={changes}
        >
          <div className="flex gap-1 flex-wrap">
            {agentTags.map((tag) => (
              <Badge key={tag} color="zinc" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        </ChangedFieldHighlight>
      )}

      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={data}
        changes={changes}
        changeType={entity.change_type}
      />

      {/* User link */}
      {typeof data.user_id === 'string' && data.user_id && (
        <div className="flex items-baseline gap-1.5 px-1 py-0.5">
          <span className="text-xs text-gray-400 shrink-0">User:</span>
          <UserLink entityId={data.user_id} className="text-sm font-normal">
            User
          </UserLink>
        </div>
      )}

      <ShowMoreSection changeType={entity.change_type}>
        {!showContentInBody && !changes.isFieldChanged('content') && content && (
          <div className="px-1 py-0.5">
            <span className="text-xs text-gray-400">Content:</span>
            <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">
              {content}
            </p>
          </div>
        )}
        {!changes.isFieldChanged('agent_tags') && agentTags.length > 0 && (
          <div className="flex items-center gap-1.5 px-1 py-0.5">
            <span className="text-xs text-gray-400">Agent Tags:</span>
            {agentTags.map((tag) => (
              <Badge key={tag} color="zinc" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}
        <KeyFieldsGridUnchanged
          fields={KEY_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
      </ShowMoreSection>
    </EntityChangeCardShell>
  );
};
